import type { Spec } from "vega";
import { chartDetailForSize, type ChartDetail } from "../fit/detail.js";
import { degradeToDetail } from "./degrade.js";
import type { JsonObject } from "./types.js";
import { isObject } from "./json.js";

export interface HostSize {
  /** Total chart height in pixels — axes, legends and title included. */
  height: number;
  /**
   * The container's measured width, used until the chart measures it itself
   * (headless render, first paint) and to decide how much detail fits. In the
   * browser the chart then tracks its container's width.
   */
  fallbackWidth: number;
  /** A fixed total width; when unset the chart tracks its container. */
  fixedWidth?: number;
  /** How much to draw; derived from the size when unset. */
  detail?: ChartDetail;
}

const containerWidth = (fallback: number) =>
  `isFinite(containerSize()[0]) ? containerSize()[0] : ${fallback}`;

/** Title text as a plain string, or null when a signal computes it. */
function plainText(text: unknown): string | null {
  if (typeof text === "string") return text;
  if (Array.isArray(text) && text.every((line) => typeof line === "string")) {
    return text.join(" ");
  }
  return null;
}

/** Adds a full-text hover tooltip to a title part, keeping author encodings. */
function withTooltip(
  encode: unknown,
  part: "title" | "subtitle",
  tooltip: string,
): JsonObject {
  const base = isObject(encode) ? encode : {};
  const entry = isObject(base[part]) ? base[part] : {};
  const update = isObject(entry.update) ? entry.update : {};
  const group = isObject(base.group) ? base.group : {};
  return {
    ...base,
    // Picking skips non-interactive marks, and titles are not interactive
    // by default — without these the tooltip never shows.
    group: { interactive: true, ...group },
    [part]: {
      interactive: true,
      ...entry,
      update: { tooltip: { value: tooltip }, ...update },
    },
  };
}

/**
 * Keeps a title inside the chart: capped at the plot width (Vega ellipsizes
 * past `limit`), with the full text as a hover tooltip so nothing is lost.
 * Author-set `limit`s and tooltips win.
 */
function fitTitle(title: unknown): unknown {
  const normalized = isObject(title)
    ? title
    : plainText(title) !== null
      ? { text: title }
      : null;
  if (!normalized) return title;
  let fitted: JsonObject = { limit: { signal: "width" }, ...normalized };
  for (const [part, key] of [
    ["title", "text"],
    ["subtitle", "subtitle"],
  ] as const) {
    const text = plainText(fitted[key]);
    if (text)
      fitted = { ...fitted, encode: withTooltip(fitted.encode, part, text) };
  }
  return fitted;
}

/**
 * Sizes a Vega spec (hand-written, or compiled from Vega-Lite) to its host
 * grid cell: `fit` autosizing so the *whole* chart — axes, legends and title
 * included — fits the given height and the container's width, rather than
 * the plot alone (Vega's default `pad` grows past the cell and gets clipped).
 */
export function fitToHost(spec: Spec, size: HostSize): Spec {
  const source = spec as unknown as JsonObject;
  const signals = Array.isArray(source.signals) ? source.signals : [];
  const hasWidthSignal = signals.some(
    (signal) => isObject(signal) && signal.name === "width",
  );
  const widthSignal = {
    name: "width",
    init: containerWidth(size.fallbackWidth),
    on: [
      { events: "window:resize", update: containerWidth(size.fallbackWidth) },
    ],
  };
  const fitted: JsonObject = {
    ...source,
    autosize: { type: "fit", contains: "padding", resize: true },
    height: size.height,
  };
  if (size.fixedWidth !== undefined) {
    fitted.width = size.fixedWidth;
  } else {
    delete fitted.width;
    fitted.signals = hasWidthSignal
      ? signals.map((signal) =>
          isObject(signal) && signal.name === "width"
            ? { ...signal, ...widthSignal }
            : signal,
        )
      : [widthSignal, ...signals];
  }
  if ("title" in fitted) fitted.title = fitTitle(fitted.title);
  const detail =
    size.detail ??
    chartDetailForSize({
      width: size.fixedWidth ?? size.fallbackWidth,
      height: size.height,
    });
  return degradeToDetail(fitted, detail) as unknown as Spec;
}
